import React, { useMemo, useRef, useState } from "react";
import { useOnClickOutside } from "usehooks-ts";
import toast from "react-hot-toast";

import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { restrictToParentElement, restrictToVerticalAxis } from "@dnd-kit/modifiers";

import { AddRegular, ArrowLeft12Filled, SaveRegular } from "@fluentui/react-icons";
import {
  Button,
  Card,
  Divider,
  Field,
  InfoLabel,
  Input,
  Spinner,
  Switch,
  Text,
  Tooltip,
  useRestoreFocusTarget,
} from "@fluentui/react-components";

import { Stack } from "@/components/stack";
import { PlaybookRuleRow } from "./components/playbook-rule-row";
import { ExitPlaybookEditorDialog } from "./components/exit-playbook-editor-dialog";

import { PlaybookRuleEditor } from "./playbook-rule-editor";
import { usePlaybookEditorStyles } from "./styles";

import { useAuth } from "@/context/auth";
import { useDocumentClassification } from "@/context/document-classification";

import { ClauseData, Playbook } from "@/types/risk-analysis";
import log from "@/utils/logger";

interface PlaybookEditorProps {
  playbook?: Playbook;
  onGoBack: () => void;
}

export function PlaybookEditor({ playbook, onGoBack }: PlaybookEditorProps) {
  const { user: auth } = useAuth();
  const { addPlaybook, editPlaybook } = useDocumentClassification();

  const restoreExitTargetAttribute = useRestoreFocusTarget();
  const [exitDialogOpen, setExitDialogOpen] = useState<[boolean, (() => void)?]>([false, undefined]);

  const [selectedPlaybookRule, setSelectedPlaybookRule] = useState<ClauseData>();
  const [page, setPage] = useState<"new-playbook-rule" | "edit-playbook-rule" | "main">("main");

  const [loading, setLoading] = useState<boolean>(false);
  const [name, setName] = useState<string>(playbook?.name || "");
  const [isEditable, setIsEditable] = useState<boolean>(playbook?.editable || false);
  const [playbookRules, setPlaybookRules] = useState<ClauseData[]>(playbook?.rules || []);
  const [isPrivate, setIsPrivate] = useState<boolean>(
    !!playbook?.ownerUserId && playbook?.ownerUserId === auth.user.id
  );

  const hasUnsavedChanges = useMemo(() => {
    const initialName = playbook?.name || "";
    const initialIsEditable = playbook?.editable || false;
    const initialIsPrivate = !!playbook?.ownerUserId && playbook?.ownerUserId === auth.user.id;
    const initialPlaybookRules = playbook?.rules || [];
    return (
      name !== initialName ||
      isEditable !== initialIsEditable ||
      isPrivate !== initialIsPrivate ||
      JSON.stringify(playbookRules) !== JSON.stringify(initialPlaybookRules)
    );
  }, [name, isPrivate, isEditable, playbookRules, playbook, auth.user.id]);

  // @NOTE: this is FAR from an ideal solution
  // but until we have the time to properly setup
  // react-router than it will serve its purpose
  const containerRef = useRef<HTMLDivElement | null>(null);
  useOnClickOutside(containerRef, (event) => {
    const allowedIds = [
      "risk-analysis",
      "questions",
      "revisions",
      "configurations",
      "yellowpad-playbooks",
      "my-playbooks",
      "settings",
    ];
    event.preventDefault();
    let el = event.target as HTMLElement | null;
    if (el?.id && allowedIds.includes(el?.id)) {
      handleExitEditor(() => el.click());
    }
  });

  const styles = usePlaybookEditorStyles();
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    try {
      setLoading(true);
      if (playbook) {
        await editPlaybook(playbook.id, name, isPrivate, isEditable, playbookRules);
      } else {
        await addPlaybook(name, isPrivate, isEditable, playbookRules);
      }
      onGoBack();
    } catch (error) {
      toast.error(
        error?.message ||
          `Something went wrong while trying to ${playbook ? "update" : "create"} the playbook. Please try again later.`
      );
      log.error("failed to create or edit playbook", "PlaybookEditor", { playbook });
    } finally {
      setLoading(false);
    }
  };

  const handleEditPlaybookRule = (playbookRule: ClauseData) => {
    setSelectedPlaybookRule(playbookRule);
    setPage("edit-playbook-rule");
  };

  const handleDeletePlaybookRule = (index: number) => {
    setPlaybookRules((prev) => [...prev.slice(0, index), ...prev.slice(index + 1)]);
  };

  const handleExitEditor = (cb?: () => void) => {
    if (hasUnsavedChanges) {
      setExitDialogOpen([true, cb]);
    } else {
      cb ? cb() : onGoBack();
    }
  };

  const handleConfirmExit = () => {
    setExitDialogOpen([false, undefined]);
    exitDialogOpen[1] ? exitDialogOpen[1]() : onGoBack();
  };

  const handleGoBack = (playbookRule?: ClauseData) => {
    if (playbookRule) {
      // look for the index of the rule
      // if found, then an existing rule is being editted
      // if not, then an new rule is being added
      const ruleIndex = playbookRules.findIndex((rule) => rule.id === playbookRule.id);
      if (ruleIndex !== -1) {
        setPlaybookRules((prev) => [...prev.slice(0, ruleIndex), playbookRule, ...prev.slice(ruleIndex + 1)]);
      } else {
        setPlaybookRules((prev) => [...prev, playbookRule]);
      }
    }
    setSelectedPlaybookRule(undefined);
    setPage("main");
  };

  const handleDragEnd = (e: DragEndEvent) => {
    const { active, over } = e;
    if (active.id !== over.id) {
      setPlaybookRules((prev) => {
        const oldIndex = prev.findIndex((rule) => rule.id === active.id);
        const newIndex = prev.findIndex((rule) => rule.id === over.id);
        const newRules = arrayMove(prev, oldIndex, newIndex);
        return newRules.map((rule, index) => ({
          ...rule,
          order: index,
        }));
      });
    }
  };

  const isCreator = !playbook || playbook?.creatorUserId === auth.user.id;
  const switchElement = (
    <Switch
      label="Private"
      labelPosition="before"
      disabled={loading || (playbook && playbook?.creatorUserId !== auth.user.id)}
      checked={isPrivate}
      onChange={(e) => setIsPrivate(e.target.checked)}
    />
  );

  if (page === "new-playbook-rule") {
    return <PlaybookRuleEditor onGoBack={handleGoBack} />;
  }
  if (page === "edit-playbook-rule" && selectedPlaybookRule) {
    return <PlaybookRuleEditor playbookRule={selectedPlaybookRule} onGoBack={handleGoBack} />;
  }
  return (
    <div ref={containerRef} className={styles.container}>
      <Button
        appearance="transparent"
        icon={<ArrowLeft12Filled />}
        onClick={() => handleExitEditor()}
        {...restoreExitTargetAttribute}
      >
        Playbook {playbook ? "Editor" : "Creator"}
      </Button>
      <Card className={styles.playbookCard}>
        <form onSubmit={handleSubmit}>
          <Field required label="Playbook Name">
            <Input
              required
              name="playbook-name"
              value={name}
              disabled={loading}
              onChange={(e) => setName(e.target.value)}
            />
          </Field>
          <Stack align="center" justify="between" className={styles.switchRow}>
            <Stack align="center" gap="0.5rem">
              {!isCreator ? (
                <Tooltip relationship="label" content="You can only change the public status of a playbook you created">
                  {switchElement}
                </Tooltip>
              ) : (
                switchElement
              )}
              {isCreator && (
                <React.Fragment>
                  <Divider vertical />
                  <InfoLabel
                    info="If enabled, any user with access to this playbook can make changes. If disabled, only you, the creator of this playbook, can make changes."
                    style={{ display: "inline-flex", flexDirection: "row", alignItems: "center" }}
                  >
                    <Switch
                      label="Editable"
                      labelPosition="before"
                      disabled={loading}
                      checked={isEditable}
                      onChange={(e) => setIsEditable(e.target.checked)}
                    />
                  </InfoLabel>
                </React.Fragment>
              )}
            </Stack>
            <Button
              type="submit"
              disabled={loading}
              appearance="primary"
              icon={loading ? <Spinner size="tiny" /> : <SaveRegular />}
            >
              Save Playbook
            </Button>
          </Stack>
        </form>
      </Card>
      <Card className={styles.playbookRulesCard}>
        <Text weight="bold">Playbook Rules Editor</Text>
        <ul className={styles.playbookRulesList}>
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            modifiers={[restrictToVerticalAxis, restrictToParentElement]}
            onDragEnd={handleDragEnd}
          >
            <SortableContext items={playbookRules} strategy={verticalListSortingStrategy}>
              {playbookRules
                .sort((a, b) => a.order - b.order)
                .map((rule, index) => (
                  <PlaybookRuleRow
                    key={rule.id}
                    id={rule.id}
                    disabled={loading}
                    playbookRule={rule}
                    className={index !== playbookRules.length - 1 && styles.playbookRuleRowBordered}
                    onEdit={() => handleEditPlaybookRule(rule)}
                    onDelete={() => handleDeletePlaybookRule(index)}
                  />
                ))}
            </SortableContext>
          </DndContext>
        </ul>
        <Button
          appearance="primary"
          disabled={loading}
          icon={<AddRegular />}
          onClick={() => setPage("new-playbook-rule")}
        >
          Add Rule
        </Button>
      </Card>
      <ExitPlaybookEditorDialog
        playbook={playbook}
        open={exitDialogOpen[0]}
        onAccept={handleConfirmExit}
        onOpenChange={(_, data) => setExitDialogOpen([data.open, undefined])}
      />
    </div>
  );
}
